import React from "react";
import { Chip } from "@mui/material";
import Box from "@mui/material/Box";
import CustomChip from "../components/CustomChip";
import {
    PAY_PLAN_APP_LABELS,
    PAY_PLAN_APPS_FOR_ADMIN,
    SIGNUP_SOURCE_LABELS,
    isStandardOrPremiumPlan,
    payPlanChipColor,
    resolvePayPlan,
} from "../../services/PayPlanUtil";

export function usedApps(row) {
    const byCode = new Map();
    for (const appCode of PAY_PLAN_APPS_FOR_ADMIN) {
        const plan = resolvePayPlan(row, appCode);
        if (!isStandardOrPremiumPlan(plan)) continue;
        byCode.set(appCode, {
            appCode,
            label: PAY_PLAN_APP_LABELS[appCode] ?? appCode,
            plan,
            countPlay: 0,
        });
    }
    const plays = Array.isArray(row?.playApps) ? row.playApps : [];
    for (const app of plays) {
        const appCode = app?.appCode;
        if (!appCode || !(Number(app.countPlay) > 0)) continue;
        const existing = byCode.get(appCode);
        if (existing) {
            existing.countPlay = Number(app.countPlay) || 0;
        } else {
            byCode.set(appCode, {
                appCode,
                label: app.label || PAY_PLAN_APP_LABELS[appCode] || appCode,
                plan: null,
                countPlay: Number(app.countPlay) || 0,
            });
        }
    }
    return Array.from(byCode.values());
}

export const NOTIFICATION_PLATFORMS = [
    { key: "ios", label: "iOS" },
    { key: "android", label: "Android" },
    { key: "web", label: "Web" },
];

function normalizePushPlatform(value) {
    const raw = String(value || "").trim().toLowerCase();
    if (raw === "ios" || raw === "apple") return "ios";
    if (raw === "android") return "android";
    if (raw === "web") return "web";
    return "";
}

const SETTINGS_NOTIFICATION_APPS = ["jigsaw_muse", "sliding_puzzle_quest", "word_wheel_quest"];

function isAppNotificationEnabled(notificationsByApp, appCode) {
    if (!notificationsByApp || typeof notificationsByApp !== "object") return true;
    const value = notificationsByApp[appCode];
    if (value === undefined || value === null) return true;
    if (value === true || value === 1 || value === "true" || value === "1") return true;
    if (value === false || value === 0 || value === "false" || value === "0") return false;
    return Boolean(value);
}

/** Settings notification option: on unless every known app is explicitly off. */
function isSettingsNotificationEnabled(notificationsByApp) {
    if (!notificationsByApp || typeof notificationsByApp !== "object") return true;
    let saw = false;
    for (const app of SETTINGS_NOTIFICATION_APPS) {
        const value = notificationsByApp[app];
        if (value === undefined || value === null) continue;
        saw = true;
        if (value === false || value === 0 || value === "false" || value === "0" || value === "no") {
            continue;
        }
        return true;
    }
    return !saw;
}

export function notificationByPlatform(row) {
    const tokens = Array.isArray(row?.pushTokens) ? row.pushTokens : [];
    return NOTIFICATION_PLATFORMS.map((platform) => {
        if (platform.key === "web") {
            return {
                ...platform,
                on: isSettingsNotificationEnabled(row?.notificationsByApp),
                registered: true,
            };
        }
        const matching = tokens.filter((token) => normalizePushPlatform(token?.platform) === platform.key);
        const on = matching.some((token) => isAppNotificationEnabled(row?.notificationsByApp, token?.appCode));
        return {
            ...platform,
            on,
            registered: matching.length > 0,
        };
    });
}

export function NotificationPlatformChips({ row }) {
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {notificationByPlatform(row).map((platform) => (
                <Chip
                    key={platform.key}
                    label={`${platform.label} ${platform.on ? "On" : "Off"}`}
                    size="small"
                    color={platform.on ? "success" : "default"}
                    variant={platform.on ? "filled" : "outlined"}
                />
            ))}
        </Box>
    );
}

export function getColumns(navigate) {
    return [
        {
            field: "id",
            headerName: "ID",
            flex: 1,
            editable: false,
        },
        {
            field: "name",
            headerName: "Name / Email",
            width: 300,
            minWidth: 260,
            flex: 0,
            editable: false,
            alignItems: "center",
            valueGetter: (_value, row) => {
                const name = typeof row?.name === "string" ? row.name.trim() : "";
                const email = typeof row?.email === "string" ? row.email.trim() : "";
                return [name, email].filter(Boolean).join(" ");
            },
            renderCell: (params) => {
                const name = typeof params.row?.name === "string" ? params.row.name.trim() : "";
                const email = typeof params.row?.email === "string" ? params.row.email.trim() : "";
                const openUser = () => {
                    navigate(`/admin/account/subscription/${params.row.id}`);
                };
                if (!name && !email) {
                    return (
                        <Box
                            onClick={openUser}
                            sx={{
                                cursor: "pointer",
                                color: "#1976d2",
                                textDecoration: "underline",
                            }}
                        >
                            —
                        </Box>
                    );
                }
                return (
                    <Box
                        onClick={openUser}
                        sx={{
                            cursor: "pointer",
                            color: "#1976d2",
                            textDecoration: "underline",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            lineHeight: 1.35,
                            py: 0.5,
                        }}
                    >
                        {name ? (
                            <Box component="span" sx={{ display: "block" }}>
                                {name}
                            </Box>
                        ) : null}
                        {email ? (
                            <Box
                                component="span"
                                sx={{
                                    display: "block",
                                    wordBreak: "break-all",
                                    color: name ? "text.secondary" : "#1976d2",
                                    textDecoration: name ? "none" : "underline",
                                    fontSize: name ? "0.8125rem" : undefined,
                                }}
                            >
                                {email}
                            </Box>
                        ) : null}
                    </Box>
                );
            },
        },
        {
            field: "emailVerified",
            headerName: "Email Verified",
            flex: 1,
            editable: false,
            alignItems: "center",
            align: "center",
            renderCell: (params) => {
                return <CustomChip yesNo value={params.value} />;
            },
        },
        {
            field: "enabled",
            headerName: "Enabled",
            flex: 1,
            editable: false,
            alignItems: "center",
            align: "center",
            renderCell: (params) => {
                return <CustomChip yesNo value={params.value} />;
            },
        },
        {
            field: "notification",
            headerName: "Notification",
            flex: 1.2,
            minWidth: 210,
            editable: false,
            sortable: false,
            alignItems: "center",
            valueGetter: (_value, row) =>
                notificationByPlatform(row)
                    .map((p) => `${p.label} ${p.on ? "On" : "Off"}`)
                    .join(" "),
            renderCell: (params) => <NotificationPlatformChips row={params.row} />,
        },
        {
            field: "updated",
            headerName: "Updated",
            flex: 1,
            editable: false,
            alignItems: "center",
        },
        {
            field: "createdAt",
            headerName: "Created",
            flex: 1,
            editable: false,
            alignItems: "center",
        },
        {
            field: "lastLoggedInAt",
            headerName: "Last login",
            flex: 1.2,
            minWidth: 150,
            editable: false,
            alignItems: "center",
            renderCell: (params) => params.value || <span style={{ color: "#757575" }}>—</span>,
        },
        {
            field: "apps",
            headerName: "Apps",
            flex: 1.3,
            minWidth: 220,
            editable: false,
            sortable: false,
            alignItems: "center",
            valueGetter: (_value, row) => usedApps(row).map((a) => a.label).join(" "),
            renderCell: (params) => {
                const apps = usedApps(params.row);
                if (apps.length === 0) {
                    return <span style={{ color: "#757575" }}>—</span>;
                }
                return (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
                        {apps.map((app) => {
                            const paid = isStandardOrPremiumPlan(app.plan);
                            const playBit = app.countPlay > 0 ? ` ${app.countPlay}` : "";
                            const planBit = paid ? ` ${app.plan}` : "";
                            return (
                                <Chip
                                    key={app.appCode}
                                    label={`${app.label}${planBit}${playBit}`}
                                    size="small"
                                    variant={paid ? "filled" : "outlined"}
                                    color={paid ? payPlanChipColor(app.plan) : "primary"}
                                />
                            );
                        })}
                    </Box>
                );
            },
        },
        {
            field: "payPlans",
            headerName: "Pay plans",
            flex: 1.4,
            minWidth: 240,
            editable: false,
            sortable: false,
            alignItems: "center",
            renderCell: (params) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
                    {PAY_PLAN_APPS_FOR_ADMIN.map((appCode) => {
                        const plan = resolvePayPlan(params.row, appCode);
                        const label = PAY_PLAN_APP_LABELS[appCode] ?? appCode;
                        return (
                            <Chip
                                key={appCode}
                                label={`${label}: ${plan}`}
                                size="small"
                                variant="outlined"
                                color={payPlanChipColor(plan)}
                            />
                        );
                    })}
                </Box>
            ),
        },
        {
            field: "playApps",
            headerName: "Play Records",
            flex: 1.2,
            minWidth: 200,
            editable: false,
            sortable: false,
            alignItems: "center",
            valueGetter: (_value, row) => {
                const apps = Array.isArray(row?.playApps) ? row.playApps : [];
                return apps.map((a) => `${a.label} ${a.countPlay}`).join(" ");
            },
            renderCell: (params) => {
                const apps = Array.isArray(params.row?.playApps) ? params.row.playApps : [];
                if (apps.length === 0) {
                    return <span style={{ color: "#757575" }}>—</span>;
                }
                return (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
                        {apps.map((app) => (
                            <Chip
                                key={app.appCode || app.label}
                                label={`${app.label} ${app.countPlay}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                            />
                        ))}
                    </Box>
                );
            },
        },
        {
            field: "signupSource",
            headerName: "Registered",
            flex: 0.85,
            minWidth: 130,
            editable: false,
            align: "center",
            alignItems: "center",
            renderCell: (params) => {
                const v = params.value;
                if (v == null || String(v).trim() === "") {
                    return <span style={{ color: "#757575" }}>—</span>;
                }
                const key = String(v).trim().toUpperCase();
                const label = SIGNUP_SOURCE_LABELS[key] || String(v);
                return <Chip label={label} size="small" variant="outlined" />;
            },
        },
        {
            field: "stripeLivemode",
            headerName: "Stripe live mode",
            flex: 0.85,
            minWidth: 130,
            editable: false,
            align: "center",
            alignItems: "center",
            renderCell: (params) => {
                const v = params.value;
                if (v == null) {
                    return <span style={{ color: "#757575" }}>—</span>;
                }
                return (
                    <Chip
                        label={v ? "Live" : "Test"}
                        color={v ? "success" : "default"}
                        size="small"
                        variant={v ? "filled" : "outlined"}
                    />
                );
            },
        },
    ];
}
